import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";
import CustomTooltip from "./CustomTooltip";

export type DropdownStaticItem = {
  label: string;
  value: unknown;
  badgeText?: string;
};

type DropdownRecord = {
  label?: string;
  value?: unknown;
  title?: string;
  expertName?: string;
  templateName?: string;
  badgeText?: string;
  disabled?: boolean;
};

interface Props<T> {
  title: string;
  onItemSelect: (value: T | DropdownStaticItem) => void;
  list: T[];
  selectedValue: T | DropdownStaticItem | null | undefined;
  isDisabled?: boolean;
  type?: "custom_brief_templates" | null;
  withSearch?: boolean;
  staticItem?: DropdownStaticItem;
}

const asRecord = (item: unknown): DropdownRecord | null =>
  typeof item === "object" && item !== null ? (item as DropdownRecord) : null;

const includesTerm = (text: string | undefined, term: string) =>
  (text ?? "").toLowerCase().includes(term.toLowerCase());

export const Dropdown = <T,>({
  title,
  onItemSelect,
  list,
  selectedValue,
  isDisabled,
  type,
  withSearch = true,
  staticItem
}: Props<T>) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const onClose = () => setIsOpen(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedRecord = asRecord(selectedValue);
  const badgeTone =
    selectedRecord?.badgeText === "STANDARD" ? "neutral" : "accent";

  const triggerLabel = useMemo(() => {
    let content: string;
    const record = asRecord(selectedValue);
    if (record) {
      if (record.label && record.value === undefined) {
        content = record.label;
      } else if (title === "Expert" && record.expertName) {
        content = record.expertName;
      } else if (type === "custom_brief_templates" && record.templateName) {
        content = record.templateName;
      } else {
        content = record.title || title;
      }
    } else {
      content = selectedValue ? String(selectedValue) : title;
    }
    return String(content);
  }, [selectedValue, title, type]);

  const filteredList = useMemo(() => {
    return list.filter((item) => {
      const record = asRecord(item);

      // Expert case with `expertName`
      if (title === "Expert" && record?.expertName) {
        return includesTerm(record.expertName, searchTerm);
      }

      // Object with `title` property
      if (record?.title) {
        return includesTerm(record.title, searchTerm);
      }

      // Object with `templateName` property
      if (record?.templateName) {
        return includesTerm(record.templateName, searchTerm);
      }

      // Plain strings
      if (typeof item === "string") {
        return includesTerm(item, searchTerm);
      }

      // Numbers
      if (typeof item === "number") {
        return item.toString().includes(searchTerm);
      }

      return false;
    });
  }, [list, searchTerm, title]);

  const finalList = useMemo<(T | DropdownStaticItem)[]>(() => {
    const baseList = filteredList;
    if (staticItem) {
      return [staticItem, ...baseList];
    }
    return baseList;
  }, [filteredList, staticItem]);

  const renderMenuItem = (item: T | DropdownStaticItem) => {
    let content: string | undefined;
    const record = asRecord(item);

    if (record) {
      if (record.label && record.value === undefined) {
        content = record.label;
      } else if (title === "Expert" && record.expertName) {
        content = record.expertName;
      } else if (type === "custom_brief_templates" && record.templateName) {
        content = record.templateName;
      } else {
        content = record.title;
      }
    } else {
      content = String(item);
    }

    return (
      <div className="flex items-center gap-2">
        <span>{content}</span>
        {record?.badgeText && (
          <StatusBadge tone={badgeTone}>{record.badgeText}</StatusBadge>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!selectedValue && staticItem) {
      onItemSelect(staticItem);
    }
  }, [selectedValue, staticItem, onItemSelect]);

  useEffect(() => {
    if (isOpen && withSearch && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, withSearch]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <CustomTooltip label={triggerLabel} wrapperClassName="block w-full">
        <PopoverTrigger asChild>
          <Button
            variant="secondary"
            disabled={isDisabled}
            className="w-full justify-start px-3"
          >
            <span className="flex w-full items-center justify-between gap-2">
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <span className="max-w-96 truncate">{triggerLabel}</span>
                {selectedRecord?.badgeText && (
                  <StatusBadge tone={badgeTone}>
                    {selectedRecord.badgeText}
                  </StatusBadge>
                )}
              </span>
              <ChevronDown className="text-fg-3" />
            </span>
          </Button>
        </PopoverTrigger>
      </CustomTooltip>

      <PopoverContent
        align="start"
        className="z-99999 w-(--radix-popover-trigger-width) gap-0 p-0"
        onOpenAutoFocus={(e) => {
          if (!withSearch) e.preventDefault();
        }}
      >
        {withSearch && (
          <div className="p-2">
            <Input
              ref={searchInputRef}
              placeholder="Search..."
              onChange={(e) => setSearchTerm(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </div>
        )}
        <div className="flex flex-col p-1">
          {finalList.map((item, index) => {
            const disabled = asRecord(item)?.disabled === true;
            return (
              <button
                type="button"
                key={index}
                disabled={disabled}
                className={cn(
                  "flex h-8 w-full items-center rounded-md px-2 text-left text-base text-fg transition-colors duration-100 outline-none",
                  disabled
                    ? "cursor-not-allowed text-fg-4"
                    : "cursor-pointer hover:bg-fill-1 focus-visible:bg-fill-2"
                )}
                onClick={() => {
                  if (disabled) return;
                  onItemSelect(item);
                  onClose();
                }}
              >
                {renderMenuItem(item)}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
